import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { reportsAPI } from "../../utils/reportsAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import ReportContent from "../../components/ReportContent";
import Pagination from "../../components/Pagination";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import { EmptyState, ErrorState } from "../../components/EmptyStates";

function AnalystReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessibleOrgs, setAccessibleOrgs] = useState([]);

  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    if (user?.organization) return user.organization;
    if (accessibleOrgs.length > 0) return accessibleOrgs[0].id;
    return null;
  }, [user, accessibleOrgs]);

  useEffect(() => {
    // Load organizations this analyst can access, so we can still show reports
    // even when the user has no primary organization set.
    let cancelled = false;
    (async () => {
      try {
        const response = await organizationsAPI.getAccessible();
        if (cancelled) return;
        const data = response?.data || response;
        const list = Array.isArray(data) ? data : data?.results || [];
        setAccessibleOrgs(list);
      } catch {
        if (!cancelled) setAccessibleOrgs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadReports(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrgId, currentPage]);

  const loadReports = async (page = 1) => {
    if (!effectiveOrgId) {
      // Allow the page to render with a friendly empty state instead of blocking.
      setReports([]);
      setPagination(null);
      setError("No organization selected");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await reportsAPI.list(effectiveOrgId, {
        page,
        page_size: 10,
      });
      const data = response?.data || response;
      const list = Array.isArray(data) ? data : data?.results || [];
      setReports(list);

      if (response?.pagination) {
        setPagination(response.pagination);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = async (report) => {
    if (!effectiveOrgId || !report?.id) return;
    const confirmed = await showConfirm({
      title: "Delete report?",
      message:
        "This action cannot be undone. The report will be removed from this organization.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });
    if (!confirmed) return;
    try {
      await reportsAPI.delete(effectiveOrgId, report.id);
      showToast.success("Report deleted successfully");
      loadReports(currentPage);
      if (selectedReport?.id === report.id) {
        setSelectedReport(null);
      }
    } catch (err) {
      showToast.error("Failed to delete report: " + err.message);
    }
  };

  const handlePublish = async (report) => {
    if (!effectiveOrgId || !report?.id) return;
    try {
      await reportsAPI.update(effectiveOrgId, report.id, {
        status: "PUBLISHED",
        submitted: true,
      });
      showToast.success("Report submitted successfully");
      loadReports(currentPage);
    } catch (err) {
      showToast.error("Failed to submit report: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <p>Loading reports...</p>
      </div>
    );
  }

  if (selectedReport) {
    return (
      <div className="page-content">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSelectedReport(null)}
          style={{ marginBottom: "16px" }}
        >
          ← Back to Reports
        </button>

        <div className="card">
          <h2 className="card-title">
            {selectedReport.title || selectedReport.name}
          </h2>
          <p style={{ color: "var(--text-grey)", marginTop: "4px" }}>
            {new Date(
              selectedReport.created_at || Date.now(),
            ).toLocaleDateString()}
            {(selectedReport.author_name || selectedReport.author_email) &&
              ` • By ${selectedReport.author_name || selectedReport.author_email}`}
          </p>

          {selectedReport.subject && (
            <div style={{ marginTop: "16px" }}>
              <h3>Subject</h3>
              <p>{selectedReport.subject}</p>
            </div>
          )}

          {selectedReport.description && (
            <div style={{ marginTop: "16px" }}>
              <h3>Description</h3>
              <p>{selectedReport.description}</p>
            </div>
          )}

          <div style={{ marginTop: "16px" }}>
            <h3>Content</h3>
            <ReportContent
              report={selectedReport}
              organizationId={effectiveOrgId}
            />
          </div>

          <div style={{ marginTop: "16px" }}>
            <h3>Status</h3>
            <span
              className={`status-badge status-${
                selectedReport.status || "completed"
              }`}
            >
              {selectedReport.status || "completed"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Reports</h2>
          <a
            href="/analyst/design-report"
            className="btn btn-primary btn-sm"
            style={{ textDecoration: "none" }}
          >
            + New Report
          </a>
        </div>
        {error ? (
          <ErrorState
            error={
              error === "No organization selected"
                ? "No organization selected. Ask an admin to assign you to an organization, or continue drafting reports from the Design Report screen."
                : error
            }
            onRetry={() => loadReports(1)}
          />
        ) : reports.length > 0 ? (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr
                    key={report.id}
                    className={report.is_seen === false ? "row-unseen" : ""}
                  >
                    <td>{report.title || report.name}</td>
                    <td>{report.author_name || report.author_email || "-"}</td>
                    <td>
                      <span
                        className={`status-badge status-${
                          report.status || "completed"
                        }`}
                      >
                        {report.status || "completed"}
                      </span>
                    </td>
                    <td>
                      {new Date(
                        report.created_at || Date.now(),
                      ).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setSelectedReport(report)}
                      >
                        View
                      </button>
                      {report.status !== "PUBLISHED" && (
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ marginLeft: "8px" }}
                          onClick={() => handlePublish(report)}
                        >
                          Submit
                        </button>
                      )}
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: "8px" }}
                        onClick={() => handleDelete(report)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination && (
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            )}
          </>
        ) : (
          <EmptyState
            title="No reports yet"
            message="Use “New Report” to design your first report. Saved reports will appear here."
          />
        )}
      </div>
    </div>
  );
}

export default AnalystReports;
/* Duplicate block accidentally appended below (commented out to fix build)
import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { reportsAPI } from "../../utils/reportsAPI";
import Pagination from "../../components/Pagination";
import { showToast } from "../../components/Toast";

function AnalystReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [user]);

  useEffect(() => {
    loadReports(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrgId, currentPage]);

  const loadReports = async (page = 1) => {
    if (!effectiveOrgId) {
      setError("No organization selected");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await reportsAPI.list(effectiveOrgId, {
        page,
        page_size: 10,
      });
      const data = response?.data || response;
      const list = Array.isArray(data) ? data : data?.results || [];
      setReports(list);

      if (response?.pagination) {
        setPagination(response.pagination);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = async (report) => {
    if (!effectiveOrgId || !report?.id) return;
    const confirmed = await showConfirm({
      title: "Delete report",
      message: "Delete this report? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });
    if (!confirmed) return;
    try {
      await reportsAPI.delete(effectiveOrgId, report.id);
      showToast.success("Report deleted successfully");
      // Reload current page
      loadReports(currentPage);
      if (selectedReport?.id === report.id) {
        setSelectedReport(null);
      }
    } catch (err) {
      showToast.error("Failed to delete report: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <p>Loading reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-primary" onClick={() => loadReports(1)}>
          Retry
        </button>
      </div>
    );
  }

  if (selectedReport) {
    return (
      <div className="page-content">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSelectedReport(null)}
          style={{ marginBottom: "16px" }}
        >
          ← Back to Reports
        </button>

        <div className="card">
          <h2 className="card-title">
            {selectedReport.title || selectedReport.name}
          </h2>
          <p style={{ color: "var(--text-grey)", marginTop: "4px" }}>
            {new Date(
              selectedReport.created_at || Date.now(),
            ).toLocaleDateString()}
            {(selectedReport.author_name || selectedReport.author_email) &&
              ` • By ${selectedReport.author_name || selectedReport.author_email}`}
          </p>

          {selectedReport.subject && (
            <div style={{ marginTop: "16px" }}>
              <h3>Subject</h3>
              <p>{selectedReport.subject}</p>
            </div>
          )}

          {selectedReport.description && (
            <div style={{ marginTop: "16px" }}>
              <h3>Description</h3>
              <p>{selectedReport.description}</p>
            </div>
          )}

          {selectedReport.content && (
            <div style={{ marginTop: "16px" }}>
              <h3>Content</h3>
              <p>{selectedReport.content}</p>
            </div>
          )}

          <div style={{ marginTop: "16px" }}>
            <h3>Status</h3>
            <span
              className={`status-badge status-${
                selectedReport.status || "completed"
              }`}
            >
              {selectedReport.status || "completed"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Reports</h2>
          <a
            href="/analyst/design-report"
            className="btn btn-primary btn-sm"
            style={{ textDecoration: "none" }}
          >
            + New Report
          </a>
        </div>
        {reports.length > 0 ? (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id}>
                    <td>{report.title || report.name}</td>
                    <td>
                      <span
                        className={`status-badge status-${
                          report.status || "completed"
                        }`}
                      >
                        {report.status || "completed"}
                      </span>
                    </td>
                    <td>
                      {new Date(
                        report.created_at || Date.now(),
                      ).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setSelectedReport(report)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: "8px" }}
                        onClick={() => handleDelete(report)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination && (
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            )}
          </>
        ) : (
          <p style={{ padding: "16px", textAlign: "center" }}>
            No reports found. Use “New Report” to create one.
          </p>
        )}
      </div>
    </div>
  );
}

export default AnalystReports;
*/
